import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Mail, User, Shield, KeyRound, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

const roleLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  superadmin: { label: 'Super Admin', variant: 'default' },
  orgadmin: { label: 'Administrateur', variant: 'default' },
  staff: { label: 'Collaborateur', variant: 'secondary' },
  readonly: { label: 'Lecture seule', variant: 'outline' },
};

export default function Team() {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const { isAdmin } = useUserRole();
  const { user } = useAuth();

  // Fetch team members (profiles with roles in the same organization)
  const { data: teamMembers = [], isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) return [];

      // Fetch profiles and user_roles separately since there's no direct FK relationship
      const [profilesResult, rolesResult] = await Promise.all([
        supabase
        .from('profiles')
          .select('id, user_id, first_name, last_name, email, created_at')
        .eq('organization_id', profile.organization_id)
          .order('created_at', { ascending: false }),
        supabase
          .from('user_roles')
          .select('user_id, role')
          .eq('organization_id', profile.organization_id),
      ]);

      if (profilesResult.error) throw profilesResult.error;
      if (rolesResult.error) throw rolesResult.error;

      // Create a map of user_id -> role for quick lookup
      const rolesMap = new Map(
        (rolesResult.data || []).map((role) => [role.user_id, role])
      );

      // Join profiles with their roles
      return (profilesResult.data || []).map((profile) => ({
        ...profile,
        user_roles: rolesMap.has(profile.user_id)
          ? [rolesMap.get(profile.user_id)]
          : [],
      }));
    },
  });

  const inviteUser = useMutation({
    mutationFn: async (formData: FormData) => {
      const email = formData.get('email') as string;
      const role = formData.get('role') as string;

      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email,
          role,
          redirectTo: `${window.location.origin}/auth/reset-password`,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { email, role };
    },
    onSuccess: ({ email, role }) => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setIsOpen(false);
      toast.success(`Invitation envoyée à ${email} avec le rôle ${roleLabels[role]?.label || role}`);
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: string }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const { error } = await supabase
        .from('user_roles')
        .upsert({
          user_id: userId,
          organization_id: profile.organization_id,
          role: newRole as any,
        }, {
          onConflict: 'user_id,organization_id',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast.success('Rôle mis à jour');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const resetPassword = useMutation({
    mutationFn: async (email: string) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Non authentifié');

      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: { 
          email,
          redirectTo: `${window.location.origin}/auth/reset-password`
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast.success('Email de réinitialisation envoyé');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de l\'envoi');
    },
  });

  const removeMember = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('remove-team-member', { body: { userId } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast.success('Membre retiré de l\'équipe');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors du retrait');
    },
  });

  const columns = [
    {
      header: 'Membre',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">
              {row.first_name || row.last_name 
                ? `${row.first_name || ''} ${row.last_name || ''}`.trim() 
                : 'Utilisateur'}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {row.email || '-'}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: 'Rôle',
      accessorKey: (row: any) => {
        const role = row.user_roles?.[0]?.role || 'readonly';
        const roleInfo = roleLabels[role] || roleLabels.readonly;
        
        if (isAdmin) {
          return (
            <Select 
              value={role} 
              onValueChange={(newRole) => updateRole.mutate({ userId: row.user_id, newRole })}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="orgadmin">Administrateur</SelectItem>
                <SelectItem value="staff">Collaborateur</SelectItem>
                <SelectItem value="readonly">Lecture seule</SelectItem>
              </SelectContent>
            </Select>
          );
        }
        
        return (
          <Badge variant={roleInfo.variant} className="flex items-center gap-1 w-fit">
            <Shield className="h-3 w-3" />
            {roleInfo.label}
          </Badge>
        );
      },
    },
    {
      header: 'Ajouté le',
      accessorKey: (row: any) => (
        <span className="text-sm text-muted-foreground">
          {format(new Date(row.created_at), 'dd MMM yyyy', { locale: fr })}
        </span>
      ),
    },
    ...(isAdmin ? [{
      header: 'Actions',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-1">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" disabled={!row.email}>
              <KeyRound className="h-4 w-4" />
              Reset MDP
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Réinitialiser le mot de passe</AlertDialogTitle>
              <AlertDialogDescription>
                Un email de réinitialisation sera envoyé à {row.email}. L'utilisateur pourra alors définir un nouveau mot de passe.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => resetPassword.mutate(row.email)}
                disabled={resetPassword.isPending}
              >
                {resetPassword.isPending ? 'Envoi...' : 'Envoyer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {row.user_id !== user?.id && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-destructive hover:text-destructive">
                <UserMinus className="h-4 w-4" />
                Retirer
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Retirer ce membre</AlertDialogTitle>
                <AlertDialogDescription>
                  {row.email} n'aura plus accès à l'organisation. Si l'invitation n'a jamais été acceptée, le compte est supprimé et vous pourrez l'inviter à nouveau.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => removeMember.mutate(row.user_id)}
                  disabled={removeMember.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {removeMember.isPending ? 'Retrait...' : 'Retirer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        </div>
      ),
    }] : []),
  ];

  return (
    <AppLayout title="Équipe" description="Gérez les membres de votre équipe">
      <PageHeader
        title="Équipe"
        description={`${teamMembers.length} membre${teamMembers.length > 1 ? 's' : ''}`}
        actions={
          isAdmin && (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Inviter un membre</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Inviter un membre</DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    inviteUser.mutate(new FormData(e.currentTarget));
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" required placeholder="collaborateur@exemple.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Rôle</Label>
                    <Select name="role" defaultValue="staff">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="orgadmin">
                          <div className="flex flex-col">
                            <span>Administrateur</span>
                            <span className="text-xs text-muted-foreground">Accès complet à toutes les fonctionnalités</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="staff">
                          <div className="flex flex-col">
                            <span>Collaborateur</span>
                            <span className="text-xs text-muted-foreground">Peut gérer les données mais pas les paramètres</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="readonly">
                          <div className="flex flex-col">
                            <span>Lecture seule</span>
                            <span className="text-xs text-muted-foreground">Peut uniquement consulter les données</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" disabled={inviteUser.isPending}>
                    {inviteUser.isPending ? 'Envoi...' : 'Envoyer l\'invitation'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <DataTable
        columns={columns}
        data={teamMembers}
        isLoading={isLoading}
        emptyMessage="Aucun membre dans l'équipe."
      />
    </AppLayout>
  );
}
